import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { darkTheme, lightTheme } from './index'

export type ThemeMode = 'light' | 'dark' | 'system'

interface ThemeContextValue {
  mode: ThemeMode
  setMode: (mode: ThemeMode) => void
  /** Actual resolved mode (never 'system') */
  resolved: 'light' | 'dark'
}

const Ctx = createContext<ThemeContextValue>({
  mode: 'dark',
  setMode: () => {},
  resolved: 'dark',
})

// eslint-disable-next-line react-refresh/only-export-components
export const useThemeMode = () => useContext(Ctx)

const STORAGE_KEY = 'app-theme'

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeRaw] = useState<ThemeMode>(
    () => (localStorage.getItem(STORAGE_KEY) as ThemeMode) || 'dark',
  )

  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')

  const setMode = (m: ThemeMode) => {
    localStorage.setItem(STORAGE_KEY, m)
    setModeRaw(m)
  }

  const resolved: 'light' | 'dark' =
    mode === 'system' ? (prefersDark ? 'dark' : 'light') : mode

  const theme = useMemo(() => (resolved === 'dark' ? darkTheme : lightTheme), [resolved])

  // Sync <meta name="color-scheme">
  useEffect(() => {
    document.documentElement.style.colorScheme = resolved
  }, [resolved])

  return (
    <Ctx.Provider value={{ mode, setMode, resolved }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </Ctx.Provider>
  )
}
